import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMaestroEntidades, deleteMaestroEntidad } from '../../../../firebase/facturacionUtils';

// COMPONENTES REUTILIZABLES DEL TABLERO DE GESTIÓN
import TableContainer from '../../TablerosGestion/common/TableContainer';
import PaginatorFooter from '../../TablerosGestion/common/PaginatorFooter';
import useTableState from '../../TablerosGestion/hooks/useTableState';

// MODALES EXISTENTES
import ModalMaestroEntidad from '../Modales/ModalMaestroEntidad';
import ModalConfirmacion from '../../../ui/ModalConfirmacion';

const IconArrowLeft = () => (
    <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
    </svg>
);

const MaestrosFinanzasTab = ({ onBack }) => {
    const navigate = useNavigate();

    // Pestañas Maestras
    const [subTab, setSubTab] = useState('clientes');
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modales
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    const getColeccionNombre = useCallback((tabKey) => {
        switch (tabKey) {
            case 'clientes': return 'empresa_clientes';
            case 'proveedores': return 'empresa_proveedores';
            case 'cuentas': return 'empresa_cuentas';
            case 'areas': return 'empresa_areas';
            case 'conceptos_facturas': return 'empresa_conceptos_facturas';
            case 'conceptos_gastos': return 'empresa_conceptos_gastos';
            default: return 'empresa_clientes';
        }
    }, []);

    const fetchCurrentMaster = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getMaestroEntidades(getColeccionNombre(subTab));
            setList(data || []);
        } catch (error) {
            console.error("Error cargando tabla maestra:", error);
        } finally {
            setLoading(false);
        }
    }, [subTab, getColeccionNombre]);

    useEffect(() => {
        fetchCurrentMaster();
    }, [fetchCurrentMaster]);

    // Motor de grilla unificado para filtrado global y paginación
    const {
        currentPage,
        filteredData,
        paginatedData,
        totalPages,
        filterValues,
        handleFilterChange,
        setCurrentPage
    } = useTableState({
        data: list,
        initialSort: { key: 'nombre', direction: 'ascending' },
        initialColumns: {},
        initialFilters: {},
        recordsPerPage: 100
    });

    const handleVolver = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/admin/facturacion-gestion');
        }
    };

    const handleOpenCreate = () => {
        setEditingItem(null);
        setShowModal(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setShowModal(true);
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;
        try {
            await deleteMaestroEntidad(getColeccionNombre(subTab), itemToDelete.id);
            setItemToDelete(null);
            fetchCurrentMaster();
        } catch (error) {
            console.error("Error al eliminar maestro:", error);
        }
    };

    const handleSearchChange = (e) => {
        handleFilterChange({ ...filterValues, search: e.target.value });
    };

    return (
        <div className="flex flex-col h-full w-full overflow-hidden bg-white font-['Inter'] antialiased text-left text-slate-900">
            
            {/* BARRA SUPERIOR DE SUB-TABS, VOLVER Y ACCIONES */}
            <header className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200 px-6 bg-white z-[110]">
                <div className="flex items-center gap-4">
                    {/* BOTÓN ATRÁS */}
                    <button 
                        type="button"
                        onClick={handleVolver} 
                        className="group flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all cursor-pointer border border-slate-200"
                        title="Volver"
                    >
                        <IconArrowLeft />
                        <span>Atrás</span>
                    </button>

                    {/* BOTONERA DE PESTAÑAS MAESTRAS */}
                    <div className="flex items-center gap-1.5 overflow-x-auto select-none py-1">
                        {[
                            { id: 'clientes', label: '🏢 Clientes' },
                            { id: 'proveedores', label: '🚚 Proveedores' },
                            { id: 'cuentas', label: '🏦 Cuentas' },
                            { id: 'areas', label: '🏷️ Áreas' },
                            { id: 'conceptos_facturas', label: '📄 Conceptos Facturas' },
                            { id: 'conceptos_gastos', label: '💡 Conceptos Gastos' }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setSubTab(tab.id)}
                                className={`px-3.5 py-2 rounded-xl text-xs font-bold uppercase transition-all cursor-pointer whitespace-nowrap ${
                                    subTab === tab.id
                                        ? 'bg-slate-900 text-white shadow-md'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* BUSCADOR RÁPIDO */}
                    <div className="relative">
                        <input
                            type="text"
                            value={filterValues.search || ''}
                            onChange={handleSearchChange}
                            placeholder="Buscar en maestros..."
                            className="border border-slate-200 bg-slate-50 pl-8 pr-3 py-2 rounded-xl font-bold text-slate-700 text-xs outline-none focus:bg-white focus:ring-2 focus:ring-slate-200 w-56 transition-all"
                        />
                        <svg className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>

                    <button
                        type="button"
                        onClick={handleOpenCreate}
                        className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-black uppercase text-xs tracking-wider shadow-md active:scale-95 transition-all cursor-pointer whitespace-nowrap"
                    >
                        + Nuevo Registro
                    </button>

                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-3 shadow-sm ml-1">
                        <div className="bg-slate-900 text-white px-3 py-1 rounded-lg text-sm font-black tabular-nums">
                            {filteredData.length}
                        </div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Registros</span>
                    </div>
                </div>
            </header>

            {/* TABLA PRINCIPAL CON ANCHO COMPLETO */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white min-h-0">
                <TableContainer minWidth="100%">
                    <thead>
                        <tr className="bg-[#01032d] text-white text-left select-none">
                            {(subTab === 'clientes' || subTab === 'proveedores') && (
                                <>
                                    <th className="p-3 px-4 text-left border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap">Razón Social / Nombre</th>
                                    <th className="p-3 px-3 text-center border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap w-36">CUIT</th>
                                    <th className="p-3 px-3 text-center border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap w-56">Condición IVA</th>
                                    <th className="p-3 px-4 text-left border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap w-52">Email</th>
                                    <th className="p-3 px-3 text-center border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap w-36">Teléfono</th>
                                </>
                            )}

                            {subTab === 'cuentas' && (
                                <>
                                    <th className="p-3 px-4 text-left border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap">Banco / Entidad</th>
                                    <th className="p-3 px-4 text-left border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap">Titular / Razón Social</th>
                                    <th className="p-3 px-4 text-left border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap w-44">Tipo de Cuenta</th>
                                    <th className="p-3 px-3 text-center border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap w-36">N° de Cuenta</th>
                                    <th className="p-3 px-3 text-center border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap w-52">CBU / CVU</th>
                                </>
                            )}

                            {subTab === 'areas' && (
                                <>
                                    <th className="p-3 px-4 text-left border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap">Nombre del Área</th>
                                    <th className="p-3 px-3 text-center border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap w-48">CUIT Asociado</th>
                                </>
                            )}

                            {subTab === 'conceptos_facturas' && (
                                <th className="p-3 px-4 text-left border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap">Concepto Facturación</th>
                            )}

                            {subTab === 'conceptos_gastos' && (
                                <>
                                    <th className="p-3 px-4 text-left border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap w-64">Concepto Padre</th>
                                    <th className="p-3 px-4 text-left border-b border-r border-slate-700 sticky top-0 bg-[#01032d] z-[50] text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap">Subconceptos Asignados</th>
                                </>
                            )}

                            {/* COLUMNA ACCIONES CON TAMAÑO RÍGIDO FIJO (150px) */}
                            <th className="w-[150px] min-w-[150px] max-w-[150px] p-3 sticky right-0 top-0 bg-[#01032d] border-b border-slate-700 z-[65] uppercase tracking-wider text-[10px] font-semibold text-center text-white whitespace-nowrap">
                                Acciones
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-semibold text-slate-800 text-left">
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="p-16 text-center text-slate-400 font-bold animate-pulse text-xs uppercase tracking-widest">
                                    Cargando registros maestros...
                                </td>
                            </tr>
                        ) : paginatedData.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="p-16 text-center text-slate-400 italic">
                                    No se encontraron registros ingresados en esta tabla maestra.
                                </td>
                            </tr>
                        ) : (
                            paginatedData.map(item => (
                                <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                    {(subTab === 'clientes' || subTab === 'proveedores') && (
                                        <>
                                            <td className="p-2.5 px-4 border-b border-r border-slate-100 font-bold text-slate-900 uppercase text-xs whitespace-nowrap truncate">{item.razonSocial || item.nombre}</td>
                                            <td className="p-2.5 px-3 border-b border-r border-slate-100 font-mono text-center text-slate-600 text-xs whitespace-nowrap">{item.cuit || '-'}</td>
                                            <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center whitespace-nowrap">
                                                <span className="bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded text-[10px] uppercase font-bold border border-slate-200">
                                                    {item.condicionIva || 'S/D'}
                                                </span>
                                            </td>
                                            <td className="p-2.5 px-4 border-b border-r border-slate-100 text-slate-600 text-xs whitespace-nowrap truncate max-w-[200px]" title={item.email}>{item.email || '-'}</td>
                                            <td className="p-2.5 px-3 border-b border-r border-slate-100 font-mono text-center text-slate-600 text-xs whitespace-nowrap">{item.telefono || '-'}</td>
                                        </>
                                    )}

                                    {subTab === 'cuentas' && (
                                        <>
                                            <td className="p-2.5 px-4 border-b border-r border-slate-100 font-bold text-slate-900 uppercase text-xs whitespace-nowrap">{item.banco}</td>
                                            <td className="p-2.5 px-4 border-b border-r border-slate-100 font-bold text-indigo-950 uppercase text-xs whitespace-nowrap">{item.titular || '-'}</td>
                                            <td className="p-2.5 px-4 border-b border-r border-slate-100 font-bold text-slate-600 text-xs whitespace-nowrap">{item.tipoCuenta || 'Cuenta Corriente'}</td>
                                            <td className="p-2.5 px-3 border-b border-r border-slate-100 font-mono text-center text-slate-600 text-xs whitespace-nowrap">{item.numeroCuenta || '-'}</td>
                                            <td className="p-2.5 px-3 border-b border-r border-slate-100 font-mono text-center text-slate-600 text-xs whitespace-nowrap">{item.cbu || '-'}</td>
                                        </>
                                    )}

                                    {subTab === 'areas' && (
                                        <>
                                            <td className="p-2.5 px-4 border-b border-r border-slate-100 font-bold text-slate-900 uppercase text-xs whitespace-nowrap">{item.nombre}</td>
                                            <td className="p-2.5 px-3 border-b border-r border-slate-100 font-mono text-center text-slate-600 text-xs whitespace-nowrap">{item.cuit || '-'}</td>
                                        </>
                                    )}

                                    {subTab === 'conceptos_facturas' && (
                                        <td className="p-2.5 px-4 border-b border-r border-slate-100 font-bold text-slate-900 uppercase text-xs whitespace-nowrap">{item.nombre}</td>
                                    )}

                                    {subTab === 'conceptos_gastos' && (
                                        <>
                                            <td className="p-2.5 px-4 border-b border-r border-slate-100 font-bold text-slate-900 uppercase text-xs whitespace-nowrap">{item.nombre}</td>
                                            <td className="p-2.5 border-b border-r border-slate-100 px-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {Array.isArray(item.subconceptos) && item.subconceptos.length > 0 ? (
                                                        item.subconceptos.map((sub, i) => (
                                                            <span key={i} className="bg-indigo-50 text-indigo-800 font-bold px-2 py-0.5 rounded text-[10px] uppercase border border-indigo-100 whitespace-nowrap">
                                                                {sub}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="text-slate-400 italic text-[10px] whitespace-nowrap">Sin subconceptos</span>
                                                    )}
                                                </div>
                                            </td>
                                        </>
                                    )}

                                    {/* CELDA ACCIONES RÍGIDA EN 150px */}
                                    <td className="w-[150px] min-w-[150px] max-w-[150px] p-2.5 px-4 border-b border-r border-slate-100 text-center sticky right-0 bg-white shadow-[-2px_0_5px_rgba(0,0,0,0.02)] whitespace-nowrap">
                                        <div className="flex justify-center gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => handleOpenEdit(item)}
                                                className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-[10px] uppercase transition-all cursor-pointer"
                                            >
                                                Editar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setItemToDelete(item)}
                                                className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-600 font-bold rounded-lg text-[10px] uppercase transition-all cursor-pointer"
                                            >
                                                Borrar
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN UNIFICADO Y PEGADO ABAJO */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="registros"
                    accentClass="text-slate-900 bg-slate-100 border-slate-200"
                />
            </main>

            {/* MODALES */}
            <ModalMaestroEntidad
                show={showModal}
                onClose={() => setShowModal(false)}
                onSuccess={fetchCurrentMaster}
                tipoEntidad={getColeccionNombre(subTab)}
                initialData={editingItem}
            />

            <ModalConfirmacion
                show={!!itemToDelete}
                onClose={() => setItemToDelete(null)}
                onConfirm={handleDelete}
                title="Eliminar Registro Maestro"
                message={`¿Está seguro de eliminar el registro "${itemToDelete?.razonSocial || itemToDelete?.nombreDisplay || itemToDelete?.banco || itemToDelete?.nombre}"?`}
            />
        </div>
    );
};

export default MaestrosFinanzasTab;